// src/pages/ResetPassword.js

import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import "../styles/ResetPassword.css";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
  const q = useQuery();
  const prefillEmail = q.get("email") || "";
  const [email, setEmail] = useState(prefillEmail);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      // POST /auth/reset-password  { email, code, password }
      await api.post("/auth/reset-password", { email, code, password });
      setNotice("Password reset successfully. You can log in now.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to reset password. Please try again.";
      setError(
        typeof msg === "string"
          ? msg
          : "Unable to reset password. Please verify your code and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-container page">
      <div className="reset-card">
        <header className="reset-header">
          <h1 className="reset-title">Reset your password</h1>
          <p className="reset-subtitle">
            Enter the 6-digit code we emailed you and choose a new password.
          </p>
        </header>

        {notice && (
          <p className="success-message" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="error-message" role="alert">
            {error}
          </p>
        )}

        <form onSubmit={submit} className="reset-form">
          <div className="reset-field-group">
            <label htmlFor="reset-email" className="reset-label">
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              required
              autoComplete="email"
              className="reset-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              placeholder="you@school.ie"
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-code" className="reset-label">
              Reset code
            </label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              placeholder="123456"
              required
              className="reset-input"
              value={code}
              onChange={(e) => setCode(e.target.value.trim())}
              disabled={loading}
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-password" className="reset-label">
              New password
            </label>
            <input
              id="reset-password"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className="reset-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="reset-field-group">
            <label htmlFor="reset-confirm" className="reset-label">
              Confirm new password
            </label>
            <input
              id="reset-confirm"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              className="reset-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
            />
          </div>

          <button
            className="primary-btn reset-primary-btn"
            type="submit"
            disabled={loading}
          >
            {loading ? "Resetting…" : "Reset password"}
          </button>
        </form>

        <div className="reset-resend">
          <button
            className="link-btn reset-link-btn"
            type="button"
            onClick={() => navigate("/forgot-password")}
          >
            Didn&apos;t get a code? Resend
          </button>
        </div>
      </div>
    </div>
  );
}
